import React from 'react';
import {
  InitialScreen,
  PatientInfoFormScreen,
  ReasonSelectionScreen,
  SchedulingAppointmentVerify,
  SchedulingProviderSelectionScreen,
} from '../../../../scheduling/components';
import { infoFormCompleted } from '../../../../scheduling/components/PatientInfoFormScreen/utils';
import {
  SchedulingDetailsStep,
  SchedulingInProgressStep,
  SchedulingTimeSelectionStep,
  SchedulingWizard,
} from '../../../../scheduling/pages/SchedulingPage/components';
import getInitialValues from '../../../../shared/components/AppointmentForm/getInitialValues';
import validateAppointmentForm from '../../../../shared/components/AppointmentForm/validateAppointment';
import getSchedulingParams from '../../../../shared/utils/getSchedulingParams';
import { getQueryFromLocation } from '../../../../shared/utils/urls';
import { ILMOffice, ILMProvider, ITrackerData, IWidgetSettings } from '../../../../types';
import { PATIENT_STATUSES, VIEW_OPENINGS_BY_OPTIONS } from '../constants';
import { TrackSchedulingEvent } from '../types';

type Props = {
  history: any;
  office: ILMOffice;
  provider?: ILMProvider;
  trackSchedulingEvent: TrackSchedulingEvent;
  trackerData: ITrackerData;
  widgetSettings: IWidgetSettings;
  hasPassedInitialScreening?: boolean;
  prefillToken: string;
};

export default function SchedulingPageBody({
  history,
  office,
  provider,
  trackSchedulingEvent,
  trackerData,
  widgetSettings,
  hasPassedInitialScreening,
  prefillToken,
}: Props) {
  const reasonsForVisit = provider?.reasonsForVisit || office.reasonsForVisit;
  const reasonsForVisitVisibleOnWidget =
    reasonsForVisit?.nodes.filter((r) => r.visibleOnWidget) || [];
  const { patientStatusId } = getQueryFromLocation(history.location);

  const schedulingParams = getSchedulingParams({
    history,
    reasonsForVisit: reasonsForVisitVisibleOnWidget,
    autoSelectPatientStatus: false,
  });

  const { minDate, selectedDate, reasonForVisit } = schedulingParams.values;

  const { viewOpeningsBy } = office;
  const showProviderSelection =
    !provider && viewOpeningsBy?.includes(VIEW_OPENINGS_BY_OPTIONS.PROVIDER);
  const { patientStatus, ...initialValues } = getInitialValues({
    provider,
    opening: null,
    widgetSettings,
    prefillToken: prefillToken as string,
    trackerData,
    isOnWidget: true,
  });
  const initialSelectedDate = selectedDate || new Date();

  return (
    <SchedulingWizard
      initialValues={{
        patientStatuses: PATIENT_STATUSES,
        reasonsForVisit: reasonsForVisitVisibleOnWidget,
        initialSelectedDate,
        selectedDate: initialSelectedDate,
        office,
        practice: office.practice,
        scheduleAfter: minDate,
        queryProvider: provider || null,
        bookingForAnother: false,
        showEditInfo: true,
        ...initialValues,
        subscriberSsn: undefined,
        reasonForVisit,
        defaultReasonForVisit: reasonForVisit,
        showProviderSelection,
        patientStatus: PATIENT_STATUSES.find(
          (p) => p.id.toLowerCase() === (patientStatusId as string)?.toLowerCase()
        ),
      }}
      validateAppointmentForm={validateAppointmentForm}
      trackSchedulingEvent={trackSchedulingEvent}
    >
      <InitialScreen
        hasPassedInitialScreening={hasPassedInitialScreening}
        completed={(values) =>
          !!values.patientGivenName && !!values.patientFamilyName && !!values.patientStatus
        }
      />
      <ReasonSelectionScreen completed={(values) => !!values.reasonForVisit} />
      {!!showProviderSelection && (
        <SchedulingProviderSelectionScreen
          completed={(values) => !!values.provider || !!values.office}
        />
      )}
      <SchedulingTimeSelectionStep
        completed={(values) => !!values.selectedDate && !!values.opening}
      />
      <PatientInfoFormScreen completed={(values) => infoFormCompleted(values)} />
      <SchedulingDetailsStep completed={(values) => !!values.termAgreedAt} />
      <SchedulingInProgressStep completed={(values) => !!values.termAgreedAt} />
      <SchedulingAppointmentVerify />
    </SchedulingWizard>
  );
}
